import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React from 'react'
import { ILocation } from 'src/types/graphql'
import { RedirectUrls } from 'src/types/types'
import { IDirectoryBreadcrumb } from './Directory'
import LocationSearchCard from './LocationSearchCard'
import Pagination from './Pagination'

interface IProps {
  page: number
  pageCount: number
  titleDir: string
  locations: ILocation[]
  breadcrumbs?: IDirectoryBreadcrumb[]
}

function DirectoryListings({
  titleDir,
  locations,
  breadcrumbs,
  page,
  pageCount
}: IProps) {
  const { t } = useTranslation('common')
  // The shared CARD_LOCATION_FIELDS fragment no longer carries the raw
  // `configurations[]` array (dropped for search-page payload size — see
  // useConfigSummary / docs/performance/large-page-data.md). Render one card
  // per location off its `defaultConfiguration` instead of expanding per unit.
  const visibleLocations = locations.filter(
    (location) =>
      !!location.defaultConfiguration &&
      (location.configSummary?.activeCount ?? 1) > 0
  )

  return (
    <div className="bg-white-500">
      {/* ── Hero strip — cream, editorial chrome ─────────────────────────── */}
      <section className="border-b border-ink-100/15 bg-cream-300">
        <div className="mx-auto max-w-7xl px-5 pt-[calc(var(--nav-h)+40px)] pb-12 md:px-10 md:pt-[calc(var(--nav-h)+64px)] md:pb-16">
          {/* Breadcrumbs — editorial small caps */}
          <nav
            aria-label="breadcrumb"
            className="mb-8 flex flex-wrap items-center gap-2 font-display text-[11px] tracking-[0.18em] text-ink-200 uppercase md:text-xs"
          >
            <Link href="/" className="transition-colors hover:text-blue-500">
              {t('DIRECTORY.HOME')}
            </Link>
            <span aria-hidden="true" className="text-ink-100/40">
              /
            </span>
            <Link
              href={RedirectUrls.Directory}
              className="transition-colors hover:text-blue-500"
            >
              {t('DIRECTORY.EYEBROW')}
            </Link>
            {breadcrumbs?.map(({ title, url }, index) => {
              const isLast = index === breadcrumbs.length - 1
              return (
                <React.Fragment key={title}>
                  <span aria-hidden="true" className="text-ink-100/40">
                    /
                  </span>
                  <Link
                    href={url}
                    className={
                      isLast
                        ? 'font-semibold text-ink-700'
                        : 'transition-colors hover:text-blue-500'
                    }
                  >
                    {title}
                  </Link>
                </React.Fragment>
              )
            })}
          </nav>

          {/* Eyebrow */}
          <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
            {t('DIRECTORY.EYEBROW')}
          </p>

          {/* Editorial H1 */}
          <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-[1.05] font-medium tracking-[-0.015em] text-ink-700 md:text-[56px]">
            {titleDir}
          </h1>

          {/* Pagination meta strip — Fraunces italic numerals */}
          <div className="mt-6 flex items-baseline gap-3 font-display text-[11px] tracking-[0.18em] text-ink-200 uppercase md:mt-8 md:text-xs">
            <span>
              <span className="font-display text-[20px] leading-none font-medium text-blue-500 italic md:text-[24px]">
                {String(page).padStart(2, '0')}
              </span>{' '}
              <span className="text-ink-100/60">/</span>{' '}
              <span className="font-display text-[20px] leading-none font-medium text-ink-700 italic md:text-[24px]">
                {String(pageCount).padStart(2, '0')}
              </span>
            </span>
          </div>
        </div>
      </section>

      {/* ── Listings grid ────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-7xl px-5 py-16 md:px-10 md:py-24">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {visibleLocations.map((location) => (
            <LocationSearchCard
              key={location.id}
              location={location}
              surface="directory"
              toggleLocation={() => {}}
              styleDefault={true}
              simpleLink={true}
            />
          ))}
        </div>

        {/* Pagination */}
        <div className="mt-14 flex w-full items-center justify-center border-t border-ink-100/15 pt-10 md:mt-20 md:pt-14">
          <Pagination
            count={pageCount}
            createHref={(p) => `${RedirectUrls.Directory}/listings/${p}`}
            page={page}
          />
        </div>

        {/* Editorial back link */}
        <div className="mt-10 flex justify-center md:mt-14">
          <Link
            href={RedirectUrls.Directory}
            className="group inline-flex items-center gap-2 font-display text-[11px] tracking-[0.18em] text-ink-700 uppercase underline decoration-blue-500/40 underline-offset-[6px] transition-colors hover:text-blue-500 hover:decoration-blue-500 md:text-xs"
          >
            ← {t('DIRECTORY.BACK_TO_FULL')}
          </Link>
        </div>
      </section>
    </div>
  )
}

export default React.memo(DirectoryListings)
